/**
 * The Construction action (rulebook p.7–8): one of
 *   deploy — a token from the supply becomes a worker in the camp
 *   build  — a worker leaves the camp for a construction site: anywhere for the
 *            player's first site, orthogonally adjacent to an own worker after
 *            that. A tile on the site is taken for its VP; the first tile of each
 *            type also grants a bonus action (R-014), resolved right after this one.
 */
import { addVp, afterAction, fail, playerOf, succeed, updatePlayer, } from '../internal.js';
import { MANIFESTS } from '../manifests.js';
/** Orthogonal neighbours of a site index on a district of `siteCount` sites (4 columns wide). */
export function siteNeighbours(index, siteCount) {
    const { columns } = MANIFESTS.constructionGrid;
    const rows = Math.ceil(siteCount / columns);
    const row = Math.floor(index / columns);
    const col = index % columns;
    const out = [];
    if (col > 0)
        out.push(index - 1);
    if (col < columns - 1)
        out.push(index + 1);
    if (row > 0)
        out.push(index - columns);
    if (row < rows - 1)
        out.push(index + columns);
    return out;
}
/** Sites this player may build on right now. */
export function buildableSites(sites, player) {
    const mine = sites.map((s, i) => (s.workers.includes(player) ? i : -1)).filter((i) => i >= 0);
    if (mine.length === 0)
        return sites.map((_, i) => i);
    const allowed = new Set();
    for (const i of mine)
        for (const n of siteNeighbours(i, sites.length))
            allowed.add(n);
    return [...allowed].sort((a, b) => a - b);
}
export function constructionOptions(state, player) {
    const out = [];
    if (player.supply > 0)
        out.push({ option: 'deploy' });
    if (player.workers > 0)
        for (const site of buildableSites(state.board.sites, player.id))
            out.push({ option: 'build', site });
    return out;
}
export function construction(state, actor, payload) {
    const p = payload;
    const player = playerOf(state, actor);
    switch (p?.option) {
        case 'deploy': {
            if (player.supply < 1)
                return fail('ILLEGAL', 'no tokens left in the supply');
            const next = updatePlayer(state, actor, (pl) => ({
                ...pl,
                supply: pl.supply - 1,
                workers: pl.workers + 1,
            }));
            return succeed(afterAction(next), [
                { type: 'TokensDeployed', player: actor, camp: 'workers', count: 1 },
            ]);
        }
        case 'build': {
            if (player.workers < 1)
                return fail('ILLEGAL', 'no worker in the camp');
            const index = p.site;
            const site = typeof index === 'number' ? state.board.sites[index] : undefined;
            if (!site)
                return fail('ILLEGAL', `no construction site ${String(index)}`);
            if (!buildableSites(state.board.sites, actor).includes(index)) {
                return fail('ILLEGAL', 'a new worker must be orthogonally adjacent to one of your workers');
            }
            const tile = site.tile;
            const sites = state.board.sites.map((s, i) => i === index ? { tile: null, workers: [...s.workers, actor] } : s);
            let next = updatePlayer({ ...state, board: { ...state.board, sites } }, actor, (pl) => ({
                ...pl,
                workers: pl.workers - 1,
                constructionTiles: tile ? [...pl.constructionTiles, tile] : pl.constructionTiles,
            }));
            const events = [
                {
                    type: 'WorkerBuilt',
                    player: actor,
                    site: index,
                    tileId: tile ? tile.id : null,
                    vp: tile?.vp ?? 0,
                },
            ];
            if (tile) {
                next = addVp(next, actor, tile.vp);
                const firstOfType = !player.constructionTiles.some((t) => t.icon === tile.icon);
                const bonus = MANIFESTS.firstOfTypeBonus[tile.icon];
                if (firstOfType && bonus) {
                    const [head, ...rest] = next.pendingActions;
                    next = {
                        ...next,
                        pendingActions: head
                            ? [head, { source: 'constructionBonus', action: bonus }, ...rest]
                            : [{ source: 'constructionBonus', action: bonus }],
                    };
                    events.push({ type: 'ConstructionBonus', player: actor, icon: tile.icon, action: bonus });
                }
            }
            return succeed(afterAction(next), events);
        }
        default:
            return fail('ILLEGAL', "Construction action needs { option: 'deploy' | 'build' }");
    }
}
//# sourceMappingURL=construction.js.map